import React from 'react';
import { Grid, Typography, Box, Paper, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import TextInput from '../../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from '../InspectionRadioGroup';
import { conditionOptions, conditionWithNaOptions, yesNoOptions } from '../../config/options';
import MediaUploadSection from '../../../../sharedModules/mediaUpload/MediaUploadSection';
import type { InspectionFormValues } from '../../utils/inspectionReportFormDefaults';
import type { ManufacturerOption } from '../../hooks/useHomeServiceManufacturers';
import { sectionHeaderSx, subHeaderSx } from './inspectionReportFormStyles';

interface ElectricalInspectionSectionProps {
  formValues: InspectionFormValues;
  updateField: (field: string, value: unknown) => void;
  electricalManufacturers: ManufacturerOption[];
  electricalPhotos: File[];
  onElectricalPhotosChange: (files: File[]) => void;
}

const ElectricalInspectionSection: React.FC<ElectricalInspectionSectionProps> = ({
  formValues,
  updateField,
  electricalManufacturers,
  electricalPhotos,
  onElectricalPhotosChange,
}) => {
  // Defensive guard: this prop can be non-array if another query wrote a different shape to cache.
  const manufacturerOptions = Array.isArray(electricalManufacturers) ? electricalManufacturers : [];

  return (
    <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      <Typography sx={sectionHeaderSx}>Electrical</Typography>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="filled">
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={formValues.electricalManufacturer}
                onChange={(e) => {
                  const label = e.target.value as string;
                  updateField('electricalManufacturer', label);
                }}
              >
                {manufacturerOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.label}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography sx={subHeaderSx}>Inspection Details</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Overall/Visual Condition"
              radioOptions={conditionOptions}
              value={formValues.electricalOverallVisualCondition}
              setValue={(val) => updateField('electricalOverallVisualCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Whole House Surge Protection"
              radioOptions={conditionWithNaOptions}
              value={formValues.electricalWholeHouseSurgeProtection}
              setValue={(val) => updateField('electricalWholeHouseSurgeProtection', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Wiring Secured / Properly Terminated"
              radioOptions={conditionOptions}
              value={formValues.electricalWiringSecuredProperlyTerminated}
              setValue={(val) => updateField('electricalWiringSecuredProperlyTerminated', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Whole Home Generator"
              radioOptions={conditionWithNaOptions}
              value={formValues.electricalWholeHomeGeneratorCondition}
              setValue={(val) => updateField('electricalWholeHomeGeneratorCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Proper Clearance Around Equipment (36 in)"
              radioOptions={yesNoOptions}
              value={formValues.electricalProperClearance}
              setValue={(val) => updateField('electricalProperClearance', val)}
            />
          </Grid>
        </Grid>

        <TextInput
          label="Electrical Notes"
          value={formValues.electricalNotes}
          valueChanged={(val) => updateField('electricalNotes', val)}
          multiline
          rows={3}
        />
        <MediaUploadSection label="Electrical Photos" files={electricalPhotos} onChange={onElectricalPhotosChange} />
      </Box>
    </Paper>
  );
};

export default ElectricalInspectionSection;
